import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Image as ImageIcon, Link as LinkIcon, ListChecks, Quote, X, Plus, Check } from "lucide-react";
import api from "@/lib/api";

// Unified capture: title + body, optional image, optional link, optional checklist,
// optional quote-mode. Saves as a single card that renders richly.
export default function CaptureDialog({ open, onOpenChange, onCreated, initialSpaceId, editing }) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [imageData, setImageData] = useState("");
  const [url, setUrl] = useState("");
  const [checklist, setChecklist] = useState([]);
  const [tags, setTags] = useState("");
  const [isQuote, setIsQuote] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setTitle(editing.title || "");
      setContent(editing.content || "");
      setImageData(editing.image_url || "");
      setUrl(editing.url || "");
      setChecklist(editing.checklist || []);
      setTags((editing.tags || []).join(", "));
      setIsQuote(editing.type === "quote");
    } else {
      setTitle(""); setContent(""); setImageData(""); setUrl("");
      setChecklist([]); setTags(""); setIsQuote(false);
    }
  }, [open, editing]);

  const pickImage = () => fileRef.current?.click();
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("Image too large (max 3MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setImageData(String(reader.result));
    reader.readAsDataURL(f);
  };

  const addChk = () => setChecklist([...checklist, { id: Math.random().toString(36).slice(2, 10), text: "", done: false }]);
  const updChk = (i, patch) => setChecklist(checklist.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  const rmChk = (i) => setChecklist(checklist.filter((_, idx) => idx !== i));

  const submit = async () => {
    const cleanList = checklist.filter(c => c.text.trim());
    const hasAny = title.trim() || content.trim() || imageData || url.trim() || cleanList.length;
    if (!hasAny) { toast.error("Add some content first"); return; }
    let type = "note";
    if (isQuote && content.trim()) type = "quote";
    else if (imageData && !content.trim() && !cleanList.length) type = "image";
    else if (url.trim() && !imageData && !cleanList.length && !content.trim()) type = "link";
    else if (cleanList.length && !content.trim() && !imageData) type = "checklist";

    const payload = {
      type,
      title: title.trim() || null,
      content: content.trim() || null,
      image_url: imageData || null,
      url: url.trim() || null,
      checklist: cleanList,
      tags: tags.split(",").map(t => t.trim()).filter(Boolean),
      space_ids: initialSpaceId ? [initialSpaceId] : (editing?.space_ids || []),
    };
    try {
      if (editing) await api.patch(`/cards/${editing.card_id}`, payload);
      else await api.post("/cards", payload);
      toast.success(editing ? "Updated" : "Captured");
      window.dispatchEvent(new Event("card:changed"));
      onOpenChange(false);
      onCreated?.();
    } catch { toast.error("Could not save"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-[color:var(--surface)] border-white/10 max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="serif-italic text-2xl">
            {editing ? "Edit note" : "Capture something"}
          </DialogTitle>
        </DialogHeader>

        <Input placeholder="Title (optional)" value={title} onChange={e => setTitle(e.target.value)} className="bg-white/5 border-white/10" data-testid="capture-title" />
        <Textarea
          placeholder="Start typing here — notes, thoughts, quotes…"
          rows={5}
          value={content}
          onChange={e => setContent(e.target.value)}
          className="bg-white/5 border-white/10"
          data-testid="capture-content"
        />

        {imageData && (
          <div className="relative">
            <img src={imageData} alt="" className="rounded-lg max-h-64 w-full object-cover" />
            <button onClick={() => setImageData("")} className="absolute top-2 right-2 bg-black/60 rounded-full p-1"><X size={14} /></button>
          </div>
        )}

        {url && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-white/5 border border-white/10">
            <LinkIcon size={13} className="text-white/50" />
            <Input value={url} onChange={e => setUrl(e.target.value)} className="border-0 bg-transparent p-0 h-auto" placeholder="https://…" data-testid="capture-url" />
            <button onClick={() => setUrl("")} className="text-white/40 hover:text-white"><X size={13} /></button>
          </div>
        )}

        {checklist.length > 0 && (
          <div className="space-y-2">
            {checklist.map((c, i) => (
              <div key={c.id} className="flex items-center gap-2">
                <button onClick={() => updChk(i, { done: !c.done })} className="w-4 h-4 rounded-full border border-white/30 grid place-items-center">
                  {c.done && <Check size={11} style={{ color: "var(--orange)" }} />}
                </button>
                <Input value={c.text} onChange={e => updChk(i, { text: e.target.value })} placeholder="Item…" className="bg-white/5 border-white/10 h-8 text-sm" />
                <button onClick={() => rmChk(i)} className="text-white/40 hover:text-white"><X size={13} /></button>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 flex-wrap">
          <button onClick={pickImage} className="pill text-xs" data-testid="add-image"><ImageIcon size={12} /> Image</button>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} data-testid="image-file-input" />
          <button onClick={() => setUrl(url || "https://")} className="pill text-xs" data-testid="add-link"><LinkIcon size={12} /> Link</button>
          <button onClick={addChk} className="pill text-xs" data-testid="add-checklist"><ListChecks size={12} /> Checklist</button>
          <button onClick={() => setIsQuote(!isQuote)} className={`pill text-xs ${isQuote ? "pill-orange" : ""}`} data-testid="toggle-quote" style={isQuote ? { background: "var(--orange)", borderColor: "var(--orange)" } : {}}>
            <Quote size={12} /> Quote
          </button>
        </div>

        <Input placeholder="tags, comma, separated" value={tags} onChange={e => setTags(e.target.value)} className="bg-white/5 border-white/10" data-testid="capture-tags" />

        <div className="flex justify-end">
          <Button onClick={submit} data-testid="capture-submit" style={{ background: "var(--orange)" }}>
            {editing ? "Save changes" : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
